import { ReactNode, useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

interface CustomerActionDialogProps {
    title: string
    children: ReactNode
    footer?: ReactNode
    busy?: boolean
    onClose: () => void
}

export function CustomerActionDialog({ title, children, footer, busy = false, onClose }: CustomerActionDialogProps) {
    const ref = useRef<HTMLDialogElement>(null)
    const titleId = useId()
    useEffect(() => {
        const dialog = ref.current
        dialog?.showModal()
        return () => dialog?.close()
    }, [])
    return (
        <dialog
            ref={ref}
            aria-labelledby={titleId}
            onCancel={(event) => {
                event.preventDefault()
                if (!busy) onClose()
            }}
            className="fixed inset-0 m-auto w-11/12 max-w-2xl max-h-[90vh] overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-slate-900 shadow-lg open:flex open:flex-col backdrop:bg-black/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:shadow-slate-900/50 dark:backdrop:bg-black/60"
        >
            <div className="mb-5 flex shrink-0 items-center justify-between gap-3">
                <h2 id={titleId} className="text-xl font-semibold">
                    {title}
                </h2>
                <button
                    type="button"
                    aria-label="Close dialog"
                    disabled={busy}
                    onClick={onClose}
                    className="rounded-md p-2 hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-50 dark:hover:bg-slate-800"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
            <div className="min-h-0 overflow-y-auto">{children}</div>
            {footer && <div className="mt-4 shrink-0 border-t border-slate-200 pt-4 dark:border-slate-700">{footer}</div>}
        </dialog>
    )
}
